import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import { AuthContext } from '@lp/core/auth';
import {
  BANNED_DRIVERS_COLUMNS,
  BANNED_DRIVERS_LIST_FILTERS_BASE,
  getRenderDriversTableCell,
} from '@lp/features/admin/tables/BannedDriversTable';
import { BannedDriversFilters } from '@lp/features/admin/tables/BannedDriversTable/components/BannedDriversFilters';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { AdminPermissions } from '@/permissions';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useBannedDriversListQuery, useRemoveBannedDriverMutation } from '@/queries/banned-drivers';
import { Driver } from '@/api/apis/resources';

export const BannedDriversList = () => {
  const navigate = useNavigate();
  const {
    data,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    isLoading,
    isFetching,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: BANNED_DRIVERS_LIST_FILTERS_BASE,
    query: useBannedDriversListQuery,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.ProvidersConfigurationUpdate);

  const { isOpen, entityData, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete<Driver>({
    query: useRemoveBannedDriverMutation,
    successMessage: 'Banned Driver deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/configuration/banned_drivers/create');
  };

  const handleEdit = useCallback(
    (bannedDriverId: string) => {
      navigate(`/admin/configuration/banned_drivers/${bannedDriverId}`);
    },
    [navigate],
  );

  const renderBannedDriversTableCell = useMemo(() => {
    return getRenderDriversTableCell(handleEdit, onModalOpen, hasCreatePermissions);
  }, [handleEdit, onModalOpen, hasCreatePermissions]);

  const BANNED_TAIL_NUMBERS_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions || hasEditPermissions
      ? BANNED_DRIVERS_COLUMNS
      : BANNED_DRIVERS_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions, hasEditPermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="Banned Drivers">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <BannedDriversFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="Banned Drivers"
            columns={BANNED_TAIL_NUMBERS_COLUMNS_FILTERED}
            renderCell={renderBannedDriversTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete Banned Driver?"
        description={
          entityData
            ? `Are you sure you want to delete ${entityData.firstName} ${entityData.lastName} from the banned drivers list?`
            : ''
        }
        highlightedText={entityData ? `${entityData.firstName} ${entityData.lastName}` : ''}
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
